import React from 'react';
import { FiShield, FiZap } from 'react-icons/fi';
import { FaRobot } from 'react-icons/fa';

const questionsUrl = new URL('../../assets/questions.jpeg', import.meta.url).href;
const hyperliquidUrl = new URL('../../assets/hyperliquid.jpeg', import.meta.url).href;
const katbotControlUrl = new URL('../../assets/katbot_control.png', import.meta.url).href;
const katbotControlJpegUrl = new URL('../../assets/katbot_control.jpeg', import.meta.url).href;

const Card = ({ children, className = '', span2 = false }) => (
  <article
    className={`bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-6
    hover:border-white/20 transition-colors duration-300 h-full ${span2 ? 'lg:col-span-2' : ''} ${className}`}
  >
    {children}
  </article>
);

const CapabilitiesGrid = () => {
  return (
    <section aria-labelledby="capabilities-heading" className="py-10 md:py-6 bg-slate-800/50 w-full flex justify-center">
      <div className="w-full px-3 sm:px-6 max-w-[1200px] lg:items-center lg:justify-center">
        {/* Section header */}
        <div className="text-center mb-8">
          <h2 id="capabilities-heading" className="bg-gradient-to-r from-green-300 via-blue-500 to-purple-600 bg-clip-text text-transparent text-5xl sm:text-4xl lg:text-6xl font-extrabold">
            Katbot is Capable
          </h2>
          <p className="text-white mt-2 max-w-2xl mx-auto">
            Research, setup, and execute — all under your control.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {/* Card 1: AI Research */}
          <Card>
            <div className="flex flex-col gap-3">
              <img
                src={questionsUrl}
                alt="AI Research"
                className="rounded-xl w-full h-60 object-cover"
              />
              <div>
                <h3 className="text-lg font-bold text-white lg:text-2xl">Agentic AI Research & Setups</h3>
                <p className="text-gray-400 mt-1 leading-relaxed">
                  Katbot combines <span className="text-cyan-300 font-semibold">Agentic AI</span>, <span className="text-white font-semibold">trading intelligence</span>, <span className="text-purple-300 font-semibold">on-chain analytics</span>, and <span className="text-green-300 font-semibold">disciplined risk management</span> to identify <span className="text-orange-300 font-semibold">opportunities and high-conviction trades</span>. Its adaptive AI agent continuously learns from market data, news, trends and sentiment to surface opportunities others miss.
                </p>
              </div>
            </div>
          </Card>

          {/* Card 2: Hyperliquid Perpetuals */}
          <Card>
            <img
              src={hyperliquidUrl}
              alt="Hyperliquid"
              className="rounded-xl w-full h-60 object-cover mb-3"
            />
            <h3 className="text-lg font-bold text-white lg:text-2xl">Hyperliquid Perpetuals</h3>
            <p className="text-gray-400 mt-1 leading-relaxed">
              Specialized execution for <span className="text-cyan-300 font-semibold">leveraged perpetuals</span> on <span className="text-cyan-300 font-semibold">Hyperliquid</span>. Live and paper trading with advanced order types—<span className="text-orange-300 font-semibold">stop-loss, take-profit, and trailing orders</span>—across 100+ assets. Deliver <span className="text-green-300 font-semibold">institutional-grade strategy and automation</span> to decentralized markets.
            </p>
          </Card>

          {/* Card 5: Order Automation */}
          <Card>
            <img
              src={katbotControlUrl}
              alt="You Stay in Control"
              className="rounded-xl w-full h-60 object-cover mb-3"
            />
            <h3 className="text-lg font-bold text-white lg:text-2xl">Order Automation with Control</h3>
            <p className="text-gray-400 mt-1 leading-relaxed">
              Specialized <span className="text-purple-300 font-semibold">order automation</span> for leveraged perpetuals. From recommendation to <span className="text-orange-300 font-semibold">execution with precision</span>—in seconds. One-click order placement with builder fees, agent delegation, and structured execution. Eliminate workflow friction and capture opportunities instantly on Hyperliquid. <span className='font-bold text-white'>Trades only happen when you like what you see.</span>
            </p>

          </Card>

          {/* Agentic */}
          <Card>
            <img
              src={katbotControlJpegUrl}
              alt="Agentic Trading Platform"
              className="rounded-xl w-full h-60 object-cover mb-4"
            />

            {/* Agent compatibility badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              {["OpenClaw", "Claude Code", "LangChain", "PydanticAI","APIs"].map((agent) => (
                <span
                  key={agent}
                  className="text-xs font-mono px-2 py-0.5 rounded-full border border-green-600/40 text-green-400 bg-green-500/10"
                >
                  {agent}
                </span>
              ))}
            </div>

            <h3 className="text-lg font-bold text-white lg:text-2xl">
              Built for Agentic Trading
            </h3>

            <p className="text-gray-400 mt-2 leading-relaxed">
              If you want agentic trading automation plug in your agent framework of choice and pair it with Katbot's recommendation engine to generate intelligent research, enforce risk guidelines, and automate order execution with full confidence of <span className="text-orange-300 font-semibold">responsible asset allocation</span> and <span className="text-green-300 font-semibold">risk management</span>.
            </p>
          </Card>


        </div>
      </div>
    </section>
  );
};

export default CapabilitiesGrid;
